import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Click, Link as LinkT } from "shared";
import { ApiException } from "@/api/client";
import { createBlock } from "@/api/blocks";
import {
	deleteLink,
	getLink,
	listClicks,
	updateLink,
} from "@/api/links";
import { QRCode } from "@/components/QRCode";
import {
	copyText,
	formatDt,
	shortUrl,
	statusClass,
	statusLabel,
} from "@/lib/format";
import { parseUserAgent } from "@/lib/ua";

export function LinkDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [link, setLink] = useState<LinkT | null>(null);
	const [clicks, setClicks] = useState<Click[]>([]);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(false);
	const [targetUrl, setTargetUrl] = useState("");
	const [startsAt, setStartsAt] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [busy, setBusy] = useState(false);
	const [copied, setCopied] = useState(false);
	const [blockedKeys, setBlockedKeys] = useState<Set<string>>(new Set());

	const load = useCallback(async () => {
		if (!id) return;
		setLoading(true);
		setError(null);
		try {
			const { link: l } = await getLink(id);
			setLink(l);
			setTargetUrl(l.targetUrl);
			setStartsAt(l.startsAt ?? "");
			setExpiresAt(l.expiresAt ?? "");
			const c = await listClicks(id);
			setClicks(c.clicks);
			setNextCursor(c.nextCursor);
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Failed to load",
			);
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		void load();
	}, [load]);

	async function onSave(e: FormEvent) {
		e.preventDefault();
		if (!link) return;
		setBusy(true);
		setError(null);
		try {
			const { link: updated } = await updateLink(link.id, {
				targetUrl,
				startsAt: startsAt || null,
				expiresAt: expiresAt || null,
			});
			setLink(updated);
			setEditing(false);
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Save failed",
			);
		} finally {
			setBusy(false);
		}
	}

	async function onToggle() {
		if (!link) return;
		try {
			const { link: updated } = await updateLink(link.id, {
				active: !link.active,
			});
			setLink(updated);
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Update failed",
			);
		}
	}

	async function onArchiveToggle() {
		if (!link) return;
		try {
			const { link: updated } = await updateLink(link.id, {
				archived: !link.archived,
			});
			setLink(updated);
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Update failed",
			);
		}
	}

	async function onDelete() {
		if (!link) return;
		if (!confirm(`Delete /${link.hash}?`)) return;
		try {
			await deleteLink(link.id);
			navigate("/links");
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Delete failed",
			);
		}
	}

	async function loadMore() {
		if (!id || !nextCursor) return;
		const c = await listClicks(id, { cursor: nextCursor });
		setClicks((prev) => [...prev, ...c.clicks]);
		setNextCursor(c.nextCursor);
	}

	async function blockFromClick(
		click: Click,
		kind: "ip" | "user_agent",
	) {
		const raw = kind === "ip" ? click.ip : click.userAgent;
		if (!raw) return;
		const key = `${kind}|${raw.toLowerCase()}`;
		if (blockedKeys.has(key)) return;
		if (
			!confirm(
				`Block ${kind === "ip" ? "IP" : "User-agent"} "${raw}" globally (all links)?`,
			)
		)
			return;
		try {
			await createBlock({ kind, value: raw });
			setBlockedKeys((prev) => new Set(prev).add(key));
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Block failed",
			);
		}
	}

	if (loading) {
		return <p className="text-fg-muted text-sm">Loading…</p>;
	}
	if (!link) {
		return (
			<div className="space-y-2">
				<p className="text-fg-muted">{error ?? "Not found"}</p>
				<Link to="/links" className="text-accent hover:underline text-sm">
					Back
				</Link>
			</div>
		);
	}

	const url = shortUrl(link.hash);

	return (
		<div className="space-y-8">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<p className="text-sm text-fg-muted">
						<Link to="/links" className="hover:underline">
							Links
						</Link>{" "}
						/
					</p>
					<h1 className="mt-1 font-mono text-xl text-accent">
						/{link.hash}
					</h1>
					<p className="mt-1 text-sm text-fg-muted break-all">
						{link.targetUrl}
					</p>
					<div className="mt-2 flex flex-wrap items-center gap-2">
						<span
							className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(link.status)}`}
						>
							{statusLabel(link.status)}
						</span>
						{link.archived && (
							<span className="rounded-full bg-indigo-500/15 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-400">
								Archived
							</span>
						)}
						<span className="text-xs text-fg-muted font-mono">
							{link.clickCount} clicks
						</span>
					</div>
				</div>
				<div className="flex flex-wrap gap-2">
					<button
						type="button"
						className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-raised"
						onClick={async () => {
							const ok = await copyText(url);
							if (ok) {
								setCopied(true);
								setTimeout(() => setCopied(false), 1500);
							}
						}}
					>
						{copied ? "Copied" : "Copy URL"}
					</button>
					<button
						type="button"
						className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-raised"
						onClick={() => void onToggle()}
					>
						{link.active ? "Disable" : "Enable"}
					</button>
					<button
						type="button"
						className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-raised"
						onClick={() => setEditing((v) => !v)}
					>
						{editing ? "Cancel" : "Edit"}
					</button>
					<button
						type="button"
						className="rounded-lg border border-indigo-500/40 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400"
						onClick={() => void onArchiveToggle()}
						title="Archived links are hidden from the default list (organizational; does not stop redirects)"
					>
						{link.archived ? "Unarchive" : "Archive"}
					</button>
					<button
						type="button"
						className="rounded-lg border border-rose-500/40 px-3 py-1.5 text-sm text-rose-600 dark:text-rose-400"
						onClick={() => void onDelete()}
					>
						Delete
					</button>
				</div>
			</div>

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			{editing && (
				<form
					onSubmit={onSave}
					className="space-y-3 rounded-xl border border-border bg-surface-raised p-5"
				>
					<label className="block text-sm">
						<span className="text-fg-muted">Target URL</span>
						<input
							type="url"
							required
							value={targetUrl}
							onChange={(e) => setTargetUrl(e.target.value)}
							className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40"
						/>
					</label>
					<div className="grid grid-cols-2 gap-3">
						<label className="block text-sm">
							<span className="text-fg-muted">Starts at</span>
							<input
								type="text"
								placeholder="YYYY-MM-DD HH:MM:SS"
								value={startsAt}
								onChange={(e) => setStartsAt(e.target.value)}
								className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-accent/40"
							/>
						</label>
						<label className="block text-sm">
							<span className="text-fg-muted">Expires at</span>
							<input
								type="text"
								placeholder="YYYY-MM-DD HH:MM:SS"
								value={expiresAt}
								onChange={(e) => setExpiresAt(e.target.value)}
								className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-accent/40"
							/>
						</label>
					</div>
					<button
						type="submit"
						disabled={busy}
						className="rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
					>
						Save
					</button>
				</form>
			)}

			<section className="flex flex-col items-start gap-4 rounded-xl border border-border p-5 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-sm font-medium text-fg-muted">
						QR code
					</h2>
					<p className="mt-1 max-w-sm text-xs text-fg-muted">
						Scannable code for{" "}
						<span className="font-mono break-all">{url}</span>. Scan
						with a phone camera to open the short link.
					</p>
				</div>
				<QRCode value={url} filename={`abe-lt-${link.hash}`} />
			</section>

			<section className="space-y-3">
				<h2 className="text-sm font-medium text-fg-muted">
					Click timeline
				</h2>
				<div className="overflow-x-auto rounded-xl border border-border">
					<table className="w-full min-w-[820px] text-left text-sm">
						<thead className="border-b border-border bg-surface-raised text-fg-muted">
							<tr>
								<th className="px-3 py-2 font-medium">Time</th>
								<th className="px-3 py-2 font-medium">IP</th>
								<th className="px-3 py-2 font-medium">Country</th>
								<th className="px-3 py-2 font-medium">Method</th>
								<th className="px-3 py-2 font-medium">
									Browser / OS
								</th>
								<th className="px-3 py-2 font-medium">Referer</th>
								<th className="px-3 py-2 font-medium">Actions</th>
							</tr>
						</thead>
						<tbody>
							{clicks.map((click) => {
								const ua = parseUserAgent(click.userAgent);
								const ipKey = click.ip
									? `ip|${click.ip.toLowerCase()}`
									: null;
								const uaKey = click.userAgent
									? `user_agent|${click.userAgent.toLowerCase()}`
									: null;
								const ipBlocked =
									ipKey !== null && blockedKeys.has(ipKey);
								const uaBlocked =
									uaKey !== null && blockedKeys.has(uaKey);
								return (
									<tr
										key={click.id}
										className="border-b border-border last:border-0"
									>
										<td className="px-3 py-2 text-xs text-fg-muted whitespace-nowrap">
											{formatDt(click.recordedAt)}
										</td>
										<td className="px-3 py-2 font-mono text-xs">
											{click.ip ?? "—"}
										</td>
										<td className="px-3 py-2 font-mono text-xs">
											{click.country ?? "—"}
										</td>
										<td className="px-3 py-2 font-mono text-xs">
											{click.method ?? "—"}
										</td>
										<td className="px-3 py-2 text-xs">
											{ua.browser} · {ua.os} · {ua.device}
										</td>
										<td className="max-w-[12rem] truncate px-3 py-2 text-xs text-fg-muted">
											{click.referer ?? "—"}
										</td>
										<td className="px-3 py-2">
											<div className="flex flex-col gap-1 text-xs">
												<button
													type="button"
													disabled={!click.ip || ipBlocked}
													className="text-fg-muted hover:text-rose-600 disabled:opacity-40 dark:disabled:text-rose-400/40 disabled:hover:text-fg-muted"
													onClick={() => void blockFromClick(click, "ip")}
												>
													{ipBlocked ? "IP blocked" : "Block IP"}
												</button>
												<button
													type="button"
													disabled={!click.userAgent || uaBlocked}
													className="text-fg-muted hover:text-rose-600 disabled:opacity-40 dark:disabled:text-rose-400/40 disabled:hover:text-fg-muted"
													onClick={() =>
														void blockFromClick(click, "user_agent")
													}
												>
													{uaBlocked ? "UA blocked" : "Block UA"}
												</button>
											</div>
										</td>
									</tr>
								);
							})}
							{clicks.length === 0 && (
								<tr>
									<td
										colSpan={7}
										className="px-3 py-6 text-center text-fg-muted"
									>
										No clicks yet
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
				{nextCursor && (
					<button
						type="button"
						className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
						onClick={() => void loadMore()}
					>
						Load more
					</button>
				)}
			</section>
		</div>
	);
}
